/* eslint-disable no-unused-vars */
import StateTreeGenerator, { EDGE_TRANSITION_TYPES, NODE_TYPES } from '@/utils/state-tree.generator'
import conversationModule from '@/store/modules/conversation.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import { ACTION_TYPES, EXPRESSION_TYPES, INPUT_TYPES, STATE_TYPES, STATE_ID } from '@/utils/bot-json.constants'
import { checkIsArray, checkIsNumber, checkIsObject, checkIsString } from '@/utils/preconditions'
import BotStateUtils from '@/utils/bot-state.utils'
import assoc from 'ramda/src/assoc'
import clone from 'ramda/src/clone'
import isEmpty from 'ramda/src/isEmpty'
import head from 'ramda/src/head'
import last from 'ramda/src/last'
import move from 'ramda/src/move'
import contentUtils from '@/utils/content.utils'
import { CONVERSATION_TYPES, PREVIEW_ITEM, SECTION_FOCUSES } from '@/store/constants'

import stateTreeUtils from '@/utils/state-tree.utils'
import previewUtils from '@/utils/preview.utils'

class PreviewController {
  addBubble(node, isInsideCondition, templateId, answerEndType = null) {
    const stateId = this.getTransitionsStateId(node.stateId, isInsideCondition)
    const actionId = this.generateId(stateId)

    if(conversationModule.isOutputState(stateId)) {
      conversationModule.addOutputActionToState(stateId, { type: 'text', payload: '' }, actionId)
      chatBubbleModule.selectBotBubble(actionId, stateId, SECTION_FOCUSES.TEXT)
      return stateId
    }

    if(conversationModule.stateExists(stateId)) {
      const newStateId = this.generateId('state')
      const newActionId = this.generateId(newStateId)
      const transition = conversationModule.getFirstStateTransition(stateId)
      if (!transition) {
        conversationModule.addNewOutputState(newStateId, newActionId, [])
      } else {
        const transitions = isInsideCondition && conversationModule.isConditionEndState(transition.state) ?
          [transition] :
          [conversationModule.createFindIntentTransition()]
        conversationModule.addNewOutputState(newStateId, newActionId, transitions)
      }

      this.resolveStateTransition(stateId, newStateId)
      chatBubbleModule.selectBotBubble(newActionId, newStateId, SECTION_FOCUSES.TEXT)
      return newStateId
    } else {
      if(stateTreeUtils.nodeIsPostback(node)) conversationModule.addPostback(node.parent.transition.postback, stateId)
      if(stateTreeUtils.nodeIsQuickReply(node)) this.addTransitionToQuickReply(node, stateId)
    }

    const transitions = templateId ? [] : [conversationModule.createFindIntentTransition()]
    conversationModule.addNewOutputState(stateId, actionId, transitions)

    chatBubbleModule.selectBotBubble(actionId, stateId, SECTION_FOCUSES.TEXT)
    return stateId
  }

  addTransitionToQuickReply(node, stateId) {
    const nodeParent = node.parent
    const parentStateId = nodeParent.node.stateId
    const transitionIndex = nodeParent.transition.index
    conversationModule.updateStateTransitionStateIdByIndex(parentStateId, stateId, transitionIndex)
  }

  resolveStateTransition(stateId, newStateId) {
    if(conversationModule.isConditionState(stateId)) {
      const conditionEndStateId = conversationModule.getConditionEndStateId(stateId)
      conversationModule.replaceLastTransitionWithExpressionTrue(newStateId, conditionEndStateId, INPUT_TYPES.NONE)
      const caseIndex = chatBubbleModule.getConditionCaseSelected(stateId)
      const transition = assoc('state', newStateId, conversationModule.getStateTransitions(stateId)[caseIndex])
      conversationModule.updateStateTransition(stateId, transition, caseIndex)
    } else {
      conversationModule.replaceLastTransitionWithExpressionTrue(stateId, newStateId, INPUT_TYPES.NONE)
    }
  }

  addWaitForUserInput(node, isInsideCondition, templateId = null) {
    throw Error('implement in children')
  }

  addSaveContextState(stateId, nextStateId, contextStateId, contextKey, templateId = null) {
    checkIsString(stateId, 'stateId')
    checkIsString(nextStateId, 'nextStateId')
    checkIsString(contextStateId, 'contextStateId')
    checkIsString(contextKey, 'contextKey')

    const transitionsStateId = this.getTransitionsStateId(stateId)

    const transitions = clone(conversationModule.getStateTransitions(transitionsStateId))
    conversationModule.createSaveContextState(contextStateId, contextKey, transitions)
    conversationModule.replaceLastTransitionWithExpressionTrue(transitionsStateId, contextStateId, INPUT_TYPES.USER_INPUT)
    conversationModule.replaceLastTransitionWithExpressionTrue(contextStateId, nextStateId, INPUT_TYPES.NONE)
  }

  addQuickReply(stateId, actionId, qrTitle, qrPayload, templateId = null) {
    checkIsString(stateId, 'stateId')
    checkIsString(actionId, 'actionId')
    checkIsString(qrTitle, 'qrTitle')
    checkIsString(qrPayload, 'qrPayload')

    const qrIndex = conversationModule.addQuickReplyToAction(actionId, qrTitle, qrPayload)
    conversationModule.addStateTransition(stateId, STATE_ID.REF_GLOBAL_IDLE, {
      input: INPUT_TYPES.QUICK_REPLY,
      expression: { equals: qrPayload }
    })
    chatBubbleModule.selectBotBubble(actionId, stateId, `${SECTION_FOCUSES.QUICK_REPLY}${qrIndex}`)
  }

  updateQuickReplyRequestOutcome(stateId, transitionIndex, requestOutcome, templateId = null) {
    const transitions = clone(conversationModule.getStateTransitions(stateId))
    if (requestOutcome) {
      transitions[transitionIndex].requestOutcome = requestOutcome
    } else {
      delete transitions[transitionIndex].requestOutcome
    }
    conversationModule.setStateTransitions(stateId, transitions)
  }

  addRemoteBubble(node, isInsideCondition, templateId = null, answerEndType = null) {
    const { stateId, newActionId } = this.addRemoteState(node, isInsideCondition, templateId, ACTION_TYPES.REMOTE)
    chatBubbleModule.selectRemoteBubble(stateId, newActionId)
    return stateId
  }

  addChatGPTBubble(node, isInsideCondition, templateId = null, answerEndType = null) {
    const { stateId, newActionId } = this.addRemoteState(node, isInsideCondition, templateId, ACTION_TYPES.CHAT_GPT_GENERAL)
    chatBubbleModule.selectChatGPTBubble(stateId, newActionId)
    return stateId
  }

  addRemoteState(node, isInsideCondition, templateId, actionType = ACTION_TYPES.REMOTE) {
    const stateId = node.stateId
    const transitionStateId = this.getTransitionsStateId(stateId, isInsideCondition)
    const foundTransitions = conversationModule.getStateTransitions(transitionStateId)
    const transitions = (!templateId && !foundTransitions) || isInsideCondition ?
      [conversationModule.createFindIntentTransition()] :
      foundTransitions

    if(conversationModule.stateExists(transitionStateId)) {
      const newStateId = this.generateId('remote')
      const actionPrefix = newStateId.replace('remote', actionType)
      const newActionId = this.generateId(actionPrefix)
      conversationModule.addNewRemoteState(newStateId, newActionId, transitions, actionType)
      this.resolveStateTransition(transitionStateId, newStateId)
      return { stateId: newStateId, newActionId }
    }

    const actionPrefix = transitionStateId.replace('remote', actionType)
    const newActionId = this.generateId(actionPrefix)
    conversationModule.addNewRemoteState(transitionStateId, newActionId, transitions, actionType)

    if(stateTreeUtils.nodeIsPostback(node)) conversationModule.addPostback(node.parent.transition.postback, transitionStateId)
    if(stateTreeUtils.nodeIsQuickReply(node)) this.addTransitionToQuickReply(node, transitionStateId)

    return { stateId: transitionStateId, newActionId }
  }

  removeSaveContextState(stateId, nextStateId, contextStateId, templateId = null) {
    checkIsString(stateId, 'stateId')
    checkIsString(nextStateId, 'nextStateId')
    checkIsString(contextStateId, 'contextStateId')

    const transitionsStateId = this.getTransitionsStateId(stateId)

    conversationModule.replaceLastTransitionWithExpressionTrue(transitionsStateId, nextStateId, INPUT_TYPES.USER_INPUT)
    conversationModule.removeState(contextStateId)
  }

  removeWaitForInput(stateTree, bubbleSelected, templateId = null) {
    const { nextStateId, stateId, contextStateId, validationActionId } = bubbleSelected
    const node = stateTreeUtils.findNode(stateTree, bubbleSelected)
    stateTreeUtils.selectNode(node)

    if (conversationModule.actionExists(validationActionId)) {
      conversationModule.removeAction(stateId, validationActionId)
    }

    if (conversationModule.stateExists(contextStateId)) {
      conversationModule.removeState(contextStateId)
    }

    const transitionsStateId = this.getTransitionsStateId(stateId)
    if (conversationModule.stateExists(nextStateId)) {
      conversationModule.replaceLastTransitionWithExpressionTrue(transitionsStateId, nextStateId, INPUT_TYPES.NONE)
      return
    }
    if (templateId && validationActionId && !conversationModule.getStateTransitions(nextStateId)) return

    conversationModule.replaceLastTransitionWithFindIntent(transitionsStateId)
  }

  addConditionSection(node, templateId) {
    throw Error('implement in children')
  }

  addConditionSectionWithTransitions(node, transitions) {
    checkIsArray(transitions, 'transitions')
    const stateId = this.getTransitionsStateId(node.stateId)
    const stateExists = conversationModule.stateExists(stateId)
    const conditionStateId = stateExists ? this.generateId('condition') : stateId
    const conditionEndStateId = this.generateId(conditionStateId) + '_end'

    conversationModule.addConditionEndState(conditionEndStateId, transitions)
    conversationModule.addConditionState(conditionStateId, conditionEndStateId)

    if(stateExists) {
      conversationModule.replaceLastTransitionWithExpressionTrue(stateId, conditionStateId, INPUT_TYPES.NONE)
    } else {
      if(stateTreeUtils.nodeIsPostback(node)) conversationModule.addPostback(node.parent.transition.postback, conditionStateId)
      if(stateTreeUtils.nodeIsQuickReply(node)) this.addTransitionToQuickReply(node, conditionStateId)
    }

    chatBubbleModule.selectConditionSection(conditionStateId, 0)
    return { conditionStateId, conditionEndStateId }
  }

  addCaseToCondition(conditionStateId, condition, templateId = null) {
    checkIsString(conditionStateId, 'conditionStateId')

    const caseStateId = this.generateId('case')
    const actionId = this.generateId(caseStateId)

    conversationModule.addNewOutputState(caseStateId, actionId)
    conversationModule.addStateTransition(conditionStateId, caseStateId, condition)

    const conditionEndStateId = conversationModule.getConditionEndStateId(conditionStateId)
    conversationModule.replaceLastTransition(caseStateId, conditionEndStateId, {
      input: INPUT_TYPES.NONE,
      expression: { true: {} }
    })

    chatBubbleModule.selectConditionSection(conditionStateId, 0)
    return caseStateId
  }

  addValidationAction(stateId, validationParams) {
    checkIsString(stateId, 'stateId')
    checkIsObject(validationParams, 'validationParams')

    const actionId = this.generateId(stateId)
    conversationModule.addValidationActionToState(stateId, actionId, validationParams)

    return actionId
  }

  updateCase(conditionStateId, transition, transitionIndex, templateId = null) {
    conversationModule.updateStateTransition(conditionStateId, transition, transitionIndex)
  }

  removeCaseFromCondition(conditionStateId, caseIndex, templateId = null) {
    const conditionNode = new StateTreeGenerator().generate(conversationModule.getJsons(), conversationModule.getType(), conversationModule.getId(), conditionStateId)
    this.removeSubtree(conditionNode.cases[caseIndex].node, templateId)
    conversationModule.removeTransitionIndex(conditionStateId, caseIndex)
    chatBubbleModule.selectConditionSection(conditionStateId, 0)
  }

  removeConditionSection(stateTreeNode, templateId = null) {
    this.removeBubble(stateTreeNode, templateId)
    const parentNode = stateTreeNode.parent?.node
    if(parentNode) {
      if(parentNode.conditionEndStateId) {
        const conditionEndTransitionState = conversationModule.getFirstStateTransition(parentNode.conditionEndStateId)
        if (!conversationModule.stateExists(conditionEndTransitionState)) return
      }

      const sectionFocus = previewUtils.getSectionFocusFromTransition(stateTreeNode.parent.transition)
      stateTreeUtils.selectNode(parentNode, sectionFocus)
    } else {
      const childNode = head(stateTreeNode.children).node
      const nodeType = [NODE_TYPES.CONDITION, NODE_TYPES.REMOTE].includes(childNode.type) ?
        childNode.type :
        NODE_TYPES.OUTPUT
      stateTreeUtils.selectNode({ ...stateTreeNode, type: nodeType, actionId: childNode.actionId })
    }
  }

  reorderButtons(actionId, text, buttons, quickReplies, fromIndex, toIndex) {
    checkIsString(actionId, 'actionId')
    checkIsNumber(fromIndex, 'fromIndex')
    checkIsNumber(toIndex, 'toIndex')
    const reorderedButtons = move(fromIndex, toIndex, buttons)
    const content = contentUtils.createContent(text, reorderedButtons, quickReplies)
    conversationModule.addOutputAction(actionId, content)
  }

  reorderQuickReplies(stateId, actionId, text, buttons, quickReplies, fromIndex, toIndex, templateId = null) {
    checkIsString(stateId, 'stateId')
    checkIsString(actionId, 'actionId')
    checkIsNumber(fromIndex, 'fromIndex')
    checkIsNumber(toIndex, 'toIndex')
    const reorderedQuickReplies = move(fromIndex, toIndex, quickReplies)
    const content = contentUtils.createContent(text, buttons, reorderedQuickReplies)
    conversationModule.addOutputAction(actionId, content)
    this.reorderTransitions(stateId, fromIndex, toIndex, templateId)
  }

  reorderTransitions(stateId, fromIndex, toIndex, templateId = null) {
    checkIsString(stateId, 'stateId')
    checkIsNumber(fromIndex, 'fromIndex')
    checkIsNumber(toIndex, 'toIndex')
    const transitions = conversationModule.getStateTransitions(stateId)
    const reorderedTransitions = move(fromIndex, toIndex, transitions)
    conversationModule.setStateTransitions(stateId, reorderedTransitions)
  }

  removeBubble(stateTreeNode, templateId = null) {
    for (const childEdge of stateTreeNode.children) {
      if (childEdge.transition.type === EDGE_TRANSITION_TYPES.POSTBACK) {
        this.removePostbackNode(childEdge.node, childEdge.transition.postback, stateTreeNode, templateId)
      } else if (childEdge.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY) {
        this.removeQuickReplyNode(childEdge.node, stateTreeNode.stateId, templateId)
      }
    }

    const childNode = head(stateTreeNode?.children)?.node
    const isChildNodeTypeSaveContext = childNode?.type === NODE_TYPES.SAVE_CONTEXT
    if (isChildNodeTypeSaveContext) {
      this.removeNode(childNode, templateId)
    }

    this.removeNode(stateTreeNode, templateId)

    const parentNode = stateTreeNode?.parent?.node
    if (parentNode?.type === NODE_TYPES.SAVE_CONTEXT) {
      const grandParentNode = parentNode.parent.node
      if (grandParentNode.type === NODE_TYPES.VALIDATION) {
        stateTreeUtils.selectNode(parentNode.parent.node)
      } else {
        const parentTransition = conversationModule.getFirstStateTransition(parentNode.stateId)
        chatBubbleModule.selectWaitForInput(grandParentNode.stateId, parentTransition.state, null, parentNode.stateId)
      }
      return
    }

    if (!isChildNodeTypeSaveContext && childNode?.children && childNode?.parent.transition.type !== EDGE_TRANSITION_TYPES.QUICK_REPLY && conversationModule.stateExists(childNode.stateId)) {
      stateTreeUtils.selectNode(childNode)
      return
    }

    if (parentNode) {
      if (parentNode.children[0].transition.type === EDGE_TRANSITION_TYPES.USER_INPUT) {
        const parentTransition = conversationModule.getFirstStateTransition(parentNode.stateId)
        const validationActionId = parentNode.type === NODE_TYPES.VALIDATION ? parentNode.actionId : null
        chatBubbleModule.selectWaitForInput(parentNode.stateId, parentTransition.state, validationActionId)
        return
      }

      const sectionFocus = previewUtils.getSectionFocusFromTransition(stateTreeNode.parent.transition)
      stateTreeUtils.selectNode(parentNode, sectionFocus)
      return
    }

    if (conversationModule.stateExists(stateTreeNode.stateId)) {
      // root is deleted but next state has been renamed to root
      // bubble won't be found in stateTree, stateTree will be reset in ChatPreview
      stateTreeUtils.selectNode(childNode)
      return
    }

    stateTreeUtils.selectEmpty()
  }

  removeNode(node, templateId = null) {
    if (
      node.isTemplate && ![NODE_TYPES.FIND_INTENT, NODE_TYPES.IDLE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.CONTINUE_TEMPLATE, NODE_TYPES.CONTINUE_ANSWER].includes(node.type)
    ) {
      this.removeStateFromAnswer(node, templateId)
      return
    }

    if ([NODE_TYPES.OUTPUT, NODE_TYPES.REMOTE, NODE_TYPES.CHAT_GPT, NODE_TYPES.VALIDATION].includes(node.type)) {
      conversationModule.removeAction(node.stateId, node.actionId)

      const stateActions = conversationModule.getStateActions(node.stateId)
      // don't delete the state if it still has some actions
      if (isEmpty(stateActions)) {
        // Check if this is a quick reply state that became empty and clear invalid answer end
        this._clearInvalidQuickReplyAnswerEnd(node)
        this.removeStateFromAnswer(node, templateId)
      }

      const actionId = head(stateActions)
      const action = conversationModule.getAction(node.stateId, actionId)
      if (stateActions.length === 1 && action.type === ACTION_TYPES.VALIDATION) {
        const firstChildNode = head(node.children).node
        const saveContextNode = head(firstChildNode.children).node
        const nextStateId = head(saveContextNode.children).node.stateId
        const bubbleSelected = {
          stateId: node.stateId,
          validationActionId: actionId,
          contextStateId: saveContextNode.stateId,
          nextStateId: nextStateId,
          type: PREVIEW_ITEM.WAIT_FOR_INPUT,
          replaceWithNextRefState: true // HACK: correct processing when calling templateController.removeWaitForInput
        }
        this.removeWaitForInput(node, bubbleSelected, templateId)
        conversationModule.removeState(node.stateId)
        conversationModule.renameState(nextStateId, node.stateId)
      }
    } else if ([NODE_TYPES.SAVE_CONTEXT, NODE_TYPES.CONDITION].includes(node.type)) {
      this.removeStateFromAnswer(node, templateId)
    }
  }

  _clearInvalidQuickReplyAnswerEnd(node) {
    // Check if this node is a quick reply state (parent transition is QUICK_REPLY)
    if (!node.parent || !node.parent.transition || node.parent.transition.type !== EDGE_TRANSITION_TYPES.QUICK_REPLY) {
      return
    }

    // Get the transitions FROM this quick reply state (not the transition TO it)
    const transitions = conversationModule.getStateTransitions(node.stateId)
    if (isEmpty(transitions)) {
      return
    }

    // Get the first transition (answer end transition)
    const answerEndTransition = head(transitions)
    if (!answerEndTransition) {
      return
    }

    // Check if the transition state is valid (IDLE or CONTINUE_ANSWER)
    const transitionState = answerEndTransition.state
    const isValid = transitionState === STATE_ID.REF_GLOBAL_IDLE || transitionState.startsWith('ref:answer:')
    
    // If invalid, clear the transition by setting it to FIND_INTENT (default)
    if (!isValid) {
      conversationModule.replaceLastTransitionWithFindIntent(node.stateId)
    }
  }

  removeStateFromAnswer(node, templateId = null) {
    if (node.type === NODE_TYPES.CONDITION) {
      for (const conditionCase of node.cases) {
        this.removeSubtree(conditionCase.node, templateId)
      }
    }

    const parentTransitionType = node.parent?.transition?.type
    const parentNode = node?.parent?.node
    const stateExists = conversationModule.stateExists(node.stateId)
    if(stateExists && parentNode) {
      switch(parentTransitionType) {
        case EDGE_TRANSITION_TYPES.CONDITION: {
          const updatedTransitions = this.setPrevTransitionToNextState(node.stateId, parentNode.stateId, true)
          conversationModule.setStateTransitions(parentNode.stateId, updatedTransitions)
          break
        }
        case EDGE_TRANSITION_TYPES.QUICK_REPLY:
          const stateId = node.type === NODE_TYPES.CONDITION ? node.conditionEndStateId : node.stateId
          const existingTransition = head(conversationModule.getStateTransitions(stateId))
          if (node.type === NODE_TYPES.CONDITION) {
            conversationModule.removeState(node.conditionEndStateId)
          }

          const child = last(node.children)
          const transitionIndex = parentNode.children.findIndex(c => c.node.stateId === node.stateId)
          const transition = clone(conversationModule.getStateTransitions(parentNode.stateId)[transitionIndex])
          transition.state = (() => {
            if (child.node.type === NODE_TYPES.IDLE) return STATE_ID.REF_GLOBAL_IDLE
            if (child.node.type === NODE_TYPES.CONTINUE_ANSWER) return 'ref:answer:' + child.node.answerId
            if (existingTransition?.state && conversationModule.stateExists(existingTransition.state)) return existingTransition.state
            if (conversationModule.stateExists(child.node.stateId)) return child.node.stateId
            return STATE_ID.REF_GLOBAL_IDLE
          })()

          conversationModule.updateStateTransition(parentNode.stateId, transition, transitionIndex)
          break
        case EDGE_TRANSITION_TYPES.POSTBACK:
          const postbackExists = conversationModule.postbackExists(node.parent.transition.postback)
          if (postbackExists) {
            // updating postback value (postback stateId) when postback state is removed but postback still exists
            const refAnswerId = last(node.children).node.answerId ? 'ref:answer:' + last(node.children).node.answerId : null
            const nextStateId = last(node.children).node.stateId || refAnswerId || STATE_ID.REF_GLOBAL_IDLE
            conversationModule.addPostback(node.parent.transition.postback, nextStateId)
          }
          break
        case EDGE_TRANSITION_TYPES.USER_INPUT:
        case EDGE_TRANSITION_TYPES.NONE:
          const updatedTransitions = this.setPrevTransitionToNextState(node.stateId, parentNode.stateId, false)
          conversationModule.setStateTransitions(this.getTransitionsStateId(parentNode.stateId), updatedTransitions)
          break
        default:
          throw new Error('unknown transition type')
      }
    }

    conversationModule.removeState(node.stateId)
    if (node.type === NODE_TYPES.CONDITION) {
      conversationModule.removeState(node.conditionEndStateId)
    }
  }

  removePostback(postbackKey, postbackParentNode = null, templateId = null) {
    const stateId = conversationModule.getRootJson().postbacks[postbackKey]
    const node = new StateTreeGenerator().generate(conversationModule.getJsons(), conversationModule.getType(), conversationModule.getId(), stateId)
    this.removePostbackNode(node, postbackKey, postbackParentNode, templateId)
  }

  removePostbackNode(node, postbackKey, postbackParentNode = null, templateId = null) {
    conversationModule.removePostback(postbackKey)
    this.removeSubtree(node, templateId)
  }

  _removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId = null) {
    this.removeActionQuickReply(qrStateId, qrParentStateId, qrParentStateActionId)
    const node = new StateTreeGenerator().generate(conversationModule.getJsons(), conversationModule.getType(), conversationModule.getId(), qrStateId)
    this.removeQuickReplyNode(node, qrParentStateId, templateId, qrStateId)
  }

  removeActionQuickReply(qrStateId, qrParentStateId, qrParentStateActionId) {
    const action = conversationModule.getAction(qrParentStateId, qrParentStateActionId)
    const { text, buttons, quickReplies } = contentUtils.getContent(action)

    const stateTransitions = conversationModule.getState(qrParentStateId).transitions
    const qrTransition = stateTransitions.find(transition => transition.state === qrStateId)
    const newQuickReplies = quickReplies.filter(quickReply => {
      return quickReply.payload !== qrTransition.condition.expression.equals
    })

    const content = contentUtils.createContent(text, buttons, newQuickReplies)
    conversationModule.addOutputAction(qrParentStateActionId, content)
  }

  removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId = null) {
    throw Error('implement in children')
  }

  removeQuickReplies(qrStateIds, qrParentStateId, qrParentStateActionId, templateId = null) {
    throw Error('implement in children')
  }

  removeQuickReplyNode(node, qrParentStateId, templateId, qrStateId = null) {
    if (node.type !== NODE_TYPES.EMPTY) this.removeSubtree(node, templateId)
    // Idle/findIntent nodes generated without a parent edge have no stateId (IDLE uses QR payload when parented)
    // Always prefer the transition target from the conversation JSON.
    const transitionStateId = qrStateId || this._getQuickReplyTransitionStateId(node)
    if (transitionStateId) conversationModule.removeTransition(qrParentStateId, transitionStateId)
  }

  _getQuickReplyTransitionStateId(node) {
    if (node.type === NODE_TYPES.IDLE) return STATE_ID.REF_GLOBAL_IDLE
    if (node.type === NODE_TYPES.FIND_INTENT) return STATE_ID.REF_GLOBAL_FIND_INTENT
    if (node.type === NODE_TYPES.CONTINUE_ANSWER && node.answerId) return `ref:answer:${node.answerId}`
    return node.stateId
  }

  removeSubtree(node, templateId = null) {
    if ([NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.FIND_INTENT].includes(node.type)) return

    if(node.children) {
      for(const childEdge of node.children) {
        if(childEdge.transition.type === EDGE_TRANSITION_TYPES.POSTBACK) {
          this.removePostbackNode(childEdge.node, childEdge.transition.postback, node, templateId)
        } else if(childEdge.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY) {
          this.removeQuickReplyNode(childEdge.node, node.stateId, templateId)
        } else {
          this.removeSubtree(childEdge.node, templateId)
        }
      }
    }
    this.removeNode(node, templateId)
  }

  setPrevTransitionToNextState(stateId, parentStateId, inCondition, conversationType = null, conversationId = null) {
    const parentTransitionStateId = this.getTransitionsStateId(parentStateId, false, conversationType, conversationId)
    const parentTransitions = conversationModule.getStateTransitions(inCondition ? parentStateId : parentTransitionStateId, conversationType, conversationId)
    const stateTransitionStateId = this.getTransitionsStateId(stateId, false, conversationType, conversationId)
    const stateTransitions = conversationModule.getStateTransitions(stateTransitionStateId, conversationType, conversationId)
    const transition = stateTransitions.find(t => t.condition.input !== INPUT_TYPES.QUICK_REPLY)

    return this.moveTransitions(parentTransitions, transition, stateId, inCondition, conversationModule.isSaveContextState(parentStateId))
  }

  moveTransitions(toTransitions, fromTransition, stateToReplace, inCondition = false, parentIsSaveContext = false) {
    if(isEmpty(toTransitions)) { // happens when moving transitions from templateRef to other templateRef
      return fromTransition ? [fromTransition] : []
    }

    const newTransitions = clone(toTransitions)
    const transition = newTransitions.find(t => t.state === stateToReplace)
    if(!transition) return newTransitions

    if(!fromTransition) { // happens when there's a dangling state
      if(([INPUT_TYPES.QUICK_REPLY, INPUT_TYPES.USER_INPUT].includes(transition.condition.input) || parentIsSaveContext) && !conversationModule.isTemplateRefState(stateToReplace)) {
        transition.state = this.generateId()
        return newTransitions
      } else {
        return []
      }
    }

    if([STATE_ID.REF_GLOBAL_FIND_INTENT, STATE_ID.REF_GLOBAL_IDLE].includes(fromTransition.state)) {
      if([INPUT_TYPES.QUICK_REPLY, INPUT_TYPES.USER_INPUT].includes(transition.condition.input)) {
        transition.state = this.generateId()
      } else if(!inCondition) {
        transition.state = fromTransition.state
        transition.condition = clone(fromTransition.condition)
      } else {
        transition.state = fromTransition.state
      }
    } else {
      transition.state = fromTransition.state
    }
    return newTransitions
  }

  getStateIdForAnswerEnd(answerEndStateId, isCondition, templateId = null) {
    const refTemplateId = conversationModule.isTemplateRefState(answerEndStateId) ? conversationModule.getTemplateIdByTemplateRef(answerEndStateId) : null
    if (refTemplateId) {
      const templateState = conversationModule.getTemplateStateForTemplateRef(answerEndStateId)
      if (templateState?.type === STATE_TYPES.CONDITION) {
        return conversationModule.getConditionEndStateRefId(templateState.conditionEndState, conversationModule.getStateFromRootJson(answerEndStateId).postbackSuffix, refTemplateId)
      }
    }

    if(!isCondition && conversationModule.isConditionState(answerEndStateId)) {
      return conversationModule.getConditionEndStateId(answerEndStateId)
    }

    return answerEndStateId
  }

  /**
   * Returns stateId that should be used when changing transitions. For Condition state this returns
   * the conditionEndStateId, for other state types stateId is returned.
   *
   * TODO the method might need a better name
   */
  getTransitionsStateId(stateId, isInsideCondition = false, conversationType = null, conversationId = null, templateId = null) {
    if(conversationModule.isTemplateRefState(stateId, conversationType, conversationId)) {
      const { templateState, fromTemplate, postbackSuffix } = conversationModule.getRootJson(conversationType, conversationId).states[stateId]
      const templateId = fromTemplate.replace('ref:template:', '')
      const isConditionState = conversationModule.isConditionState(templateState, CONVERSATION_TYPES.TEMPLATES, templateId)
      if (isConditionState) {
        const conditionState = conversationModule.getRootJson(CONVERSATION_TYPES.TEMPLATES, templateId).states[templateState]
        return conversationModule.getConditionEndStateRefId(conditionState.conditionEndState, postbackSuffix, templateId)
      }
      return stateId
    }

    return conversationModule.isConditionState(stateId) && !isInsideCondition ?
      conversationModule.getConditionEndStateId(stateId) :
      stateId
  }

  replaceTransition(stateId, transitionStateId) {
    const input = transitionStateId === STATE_ID.REF_GLOBAL_FIND_INTENT ? INPUT_TYPES.USER_INPUT : INPUT_TYPES.NONE
    this.replaceTransitionAndInputType(stateId, transitionStateId, input)
  }

  replaceTransitionAndInputType(stateId, transitionStateId, input) {
    const endCondition = { input, expression: { true: {} } }
    if (conversationModule.isConditionState(stateId)) {
      const transitionIndex = chatBubbleModule.getConditionCaseSelected(stateId)
      const transition = conversationModule.getStateTransitions(stateId)[transitionIndex]
      const expressionType = head(Object.keys(transition.condition.expression))

      const condition = [
        EXPRESSION_TYPES.ENUM,
        EXPRESSION_TYPES.OPERATOR_AVAILABLE,
        EXPRESSION_TYPES.TIME_BETWEEN,
        EXPRESSION_TYPES.DAYS_OF_WEEK,
        EXPRESSION_TYPES.KEY_EQUALS,
        EXPRESSION_TYPES.KEY_REGEX
      ].includes(expressionType) ?
        transition.condition :
        endCondition
      const updatedTransition = { condition, state: transitionStateId }

      conversationModule.updateStateTransition(stateId, updatedTransition, transitionIndex)
      return
    }

    conversationModule.replaceLastTransition(stateId, transitionStateId, endCondition)
  }

  // allows mocking ids in tests
  generateId(prefix = '') {
    return BotStateUtils.generateId(prefix)
  }
}

export default PreviewController
